import { Box, CircularProgress, Stack } from "@mui/material";
import { updateDubDetails } from "@/reducers/slices/dubPreviewSlice/dubPreviewSlice";
import { useAppDispatch } from "@/config/configureAppStore";
import { useCallback, useEffect, useState } from "react";
import unauthenticatedApi from "../api";
import { STATUS } from "@/constants/status";
import { useParams } from "react-router-dom";
import Preview from "../components/SharedDub/Preview";
import SharePassword from "../components/SharedDub/SharePassword";
import ShareDubLayout from "../components/SharedDub/ShareDubLayout";
import { SERVER_RESPONSES } from "@/constants/serverConstants";
import { UNKNOWN_ERROR_MESSAGE } from "@/constants/errors";
import ErrorFallback from "@/components/elements/ErrorFallback/ErrorFallback";

const SharedDub = () => {
  const [flow, setFlow] = useState<{
    status: STATUS;
    message?: string;
  }>({
    status: STATUS.IDLE,
    message: ""
  });
  const [passwordRequired, setPasswordRequired] = useState(false);
  const dispatch = useAppDispatch();
  const { shareId } = useParams();

  const fetchSharedMedia = useCallback(
    (password?: string) => {
      if (shareId) {
        setFlow({
          status: STATUS.LOADING
        });
        unauthenticatedApi
          .fetchSharedMedia({ shareId, password })
          .then((response) => {
            if (response.data) {
              setFlow({
                status: STATUS.SUCCESS
              });
              setPasswordRequired(false);
              const data = response.data.responseData;
              dispatch(
                updateDubDetails({
                  fileName: data.fileName,
                  sourceLocale: data.sourceLocale,
                  language: data.targetLocale,
                  previewUrl: data.previewUrl,
                  originalAudioUrl: data.originalAudioUrl,
                  dubbedVttUrl: data.dubbedVttUrl,
                  transcriptVttUrl: data.transcriptVttUrl,
                  audioOnly: data.audioOnly,
                  thumbnailUrl: data.thumbnailUrl
                })
              );
            } else {
              setFlow({
                status: STATUS.ERROR,
                message: UNKNOWN_ERROR_MESSAGE
              });
            }
          })
          .catch((e) => {
            if (e?.code === SERVER_RESPONSES.ACCESS_DENIED) {
              setPasswordRequired(true);
              if (password) {
                setFlow({
                  status: STATUS.ERROR
                });
              } else
                setFlow({
                  status: STATUS.IDLE
                });
              return;
            } else {
              setFlow({
                status: STATUS.ERROR,
                message: e?.extra || e?.message || UNKNOWN_ERROR_MESSAGE
              });
            }
          });
      }
    },
    [dispatch, shareId]
  );

  useEffect(() => {
    if (!passwordRequired && flow.status === STATUS.IDLE) {
      fetchSharedMedia();
    }
  }, [flow.status, fetchSharedMedia, passwordRequired]);

  const openPasswordLink = (password: string) => {
    fetchSharedMedia(password);
  };

  return (
    <ShareDubLayout>
      <Box
        display="flex"
        sx={{
          height: "100%"
        }}
      >
        <Stack
          sx={{
            height: "100%",
            width: "100%",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {passwordRequired ? (
            <SharePassword
              openPasswordLink={openPasswordLink}
              status={flow.status}
            />
          ) : flow.status === STATUS.LOADING ? (
            <CircularProgress />
          ) : null}
          {flow.status === STATUS.SUCCESS ? (
            <Preview />
          ) : flow.status === STATUS.ERROR && !passwordRequired ? (
            <ErrorFallback message={flow.message} description="" />
          ) : null}
        </Stack>
      </Box>
    </ShareDubLayout>
  );
};

export default SharedDub;
